import {
  animate,
  ImageScaleView,
  ImageScaleViewOpts,
  Transition,
  View,
} from '@play-co/timestep-core/ui';
import { animDuration } from 'src/lib/utils';
import { GenericProgressBar } from './GenericProgressBar';
import { clamp } from 'src/replicant/utils';

export class ProgressBar extends GenericProgressBar {
  /**
   * Get the current progress
   */
  public get progress() {
    return this._progress;
  }
  /**
   * Set progress based on the domain.
   */
  public set progress(value: number) {
    this.progressFill.style.width = this.toWidth(value);
    this._progress = value;

    this.emit('progress', value);
  }

  public progressFill: ImageScaleView;
  protected paddingRight = 0;
  protected minWidth = 30;

  constructor(
    opts: {
      superview: View;
      bgStyle?: Omit<ImageScaleViewOpts, 'superview'>;
      fillStyle?: Omit<ImageScaleViewOpts, 'superview'> & {
        paddingRight?: number;
      };
    } & {
      domain?: number[];
      progress?: number;
      minWidth?: number;
    },
  ) {
    super({
      superview: opts.superview,
      image: 'assets/ui/shared/frames/gauge_bg.png',
      width: 500,
      height: 88,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 20, right: 20 },
        vertical: { top: 20, bottom: 20 },
      },
      ...(opts.bgStyle ?? {}),
    });

    this.progressFill = new ImageScaleView({
      superview: this,
      image: 'assets/ui/shared/frames/gauge_fill.png',
      x: 4,
      y: 3,
      width: 0,
      height: 78,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 20, right: 20 },
        vertical: { top: 20, bottom: 20 },
      },
      ...(opts.fillStyle ?? {}),
    });

    this.minWidth = opts.minWidth ?? 30;
    this._progress = opts.progress ?? 0;
    this.domain = opts.domain ?? [0, 1];
    this.paddingRight = opts.fillStyle?.paddingRight ?? 0;
    this.updateStyle(opts);
  }

  public updateStyle(style: {
    bgStyle?: Omit<ImageScaleViewOpts, 'superview'>;
    fillStyle?: Omit<ImageScaleViewOpts, 'superview'> & {
      paddingRight?: number;
    };
  }) {
    this.updateOpts({
      ...(style.bgStyle || {}),
    });
    this.progressFill.updateOpts({
      ...(style.fillStyle || {}),
    });
    if (style.fillStyle?.paddingRight != null) {
      this.paddingRight = style.fillStyle.paddingRight;
    }
  }

  /**
   * Aniamte current progress to the given value.
   * @param value - Target value.
   * @param duration - Animation duration.
   */
  public async animateProgress(
    value: number,
    duration: number = animDuration,
    easing: Transition = animate.easeInOut,
    clear?: boolean,
  ) {
    const previousProgress = clamp(
      this._progress,
      this.domain[0],
      this.domain.at(-1) ?? 1,
    );
    this._progress = value;
    let eventAnim = animate(this);
    let anim = animate(this.progressFill);
    if (clear) {
      eventAnim = eventAnim.clear();
      anim = anim.clear();
    }

    void eventAnim
      .then(
        (current) => {
          const progress =
            previousProgress + current * (value - previousProgress);
          this.emit('progress', progress);
        },
        duration,
        easing,
      )
      .toPromise();
    await anim
      .then({ width: this.toWidth(value) }, duration, easing)
      .toPromise();
  }

  /**
   * Convert a value to width considering the domain.
   * @param value - Target value.
   * @returns
   */
  public toWidth(value: number) {
    // Normalize value.
    const normalizedProgress = this.getNormalizedProgress(value);

    // Convert to width domain.
    const [minWidth, maxWidth] = [
      this.minWidth,
      (this.style.width ?? 497) - this.paddingRight,
    ];
    return normalizedProgress
      ? Math.max(minWidth, normalizedProgress * maxWidth)
      : 0;
  }
}
